/**
 * Auth provider toggle.
 *
 * Reads VITE_AUTH_PROVIDER at build time. Defaults to "keycloak" so existing
 * deployments continue to work unchanged. Set to "workos" to activate the
 * WorkOS AuthKit flow added in Phase 3.
 *
 * The value at sign-in time is also persisted to localStorage under
 * `auth_provider` so token-refresh and sign-out paths know which backend
 * endpoint to call even if the env flag changes between page loads.
 */

export type AuthProvider = 'keycloak' | 'workos'

const STORAGE_KEY = 'auth_provider'

function readEnvProvider(): AuthProvider {
  const raw = (import.meta.env?.VITE_AUTH_PROVIDER || '').toString().toLowerCase()
  return raw === 'workos' ? 'workos' : 'keycloak'
}

/** Provider selected by build-time env var. Used at login entry. */
export function getEnvAuthProvider(): AuthProvider {
  return readEnvProvider()
}

/**
 * Provider associated with the currently active session.
 *
 * Falls back to env provider if no session token is present (pre-login state),
 * or to claim-shape inference if the legacy session predates the
 * `auth_provider` stamp.
 */
export function getAuthProvider(): AuthProvider {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored === 'workos' || stored === 'keycloak') return stored
  } catch {
    /* localStorage may be unavailable in some test envs */
  }
  return inferProviderFromAccessToken() || readEnvProvider()
}

export function setAuthProvider(provider: AuthProvider): void {
  try {
    localStorage.setItem(STORAGE_KEY, provider)
  } catch {
    /* ignore */
  }
}

export function clearAuthProvider(): void {
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch {
    /* ignore */
  }
}

/**
 * Infer provider from JWT claim shape on legacy sessions that pre-date the
 * `auth_provider` localStorage stamp. WorkOS subjects start with `user_`;
 * Keycloak uses UUIDs.
 */
function inferProviderFromAccessToken(): AuthProvider | null {
  try {
    const token = localStorage.getItem('access_token')
    if (!token) return null
    const parts = token.split('.')
    if (parts.length < 2) return null
    const payload = JSON.parse(atob(parts[1]))
    if (typeof payload.sub === 'string' && payload.sub.startsWith('user_')) return 'workos'
    if (payload.realm_access || payload.preferred_username) return 'keycloak'
    if (typeof payload.role === 'string' && Array.isArray(payload.permissions)) return 'workos'
  } catch {
    /* ignore */
  }
  return null
}
